/**
 * Fetches and caches Stripe platform account IDs from the backend.
 *
 * Call `initStripePlatformAccounts()` once on admin app startup (e.g., in
 * AdminAppProvider). After that, `getPlatformAccountId(country)` returns
 * the cached ID synchronously.
 *
 * If the fetch hasn't completed yet (or failed), `getPlatformAccountId`
 * returns `null` and the caller gracefully degrades (e.g., omits the
 * platform prefix from Stripe dashboard URLs).
 */

import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";

const client = generateClient<Schema>();

type Country = "CA" | "US";

let cachedAccounts: { ca: string; us: string } | null = null;
let initPromise: Promise<void> | null = null;

/**
 * Kick off the platform-accounts fetch. Safe to call multiple times —
 * subsequent calls return the same promise.
 */
export function initStripePlatformAccounts(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { data, errors } = await client.queries.getStripePlatformAccounts(
        {},
      );
      if (errors?.length) {
        console.error(
          "[stripe-platform-accounts] Query failed:",
          errors[0].message,
        );
        return;
      }
      if (data) {
        cachedAccounts = { ca: data.ca, us: data.us };
      }
    } catch (err) {
      console.error("[stripe-platform-accounts] Failed to fetch:", err);
      // Silent failure — links degrade gracefully without platform prefix.
    }
  })();

  return initPromise;
}

/**
 * Returns the platform account ID for the given country, or `null` if
 * not yet fetched (or fetch failed).
 */
export function getPlatformAccountId(country: Country): string | null {
  if (!cachedAccounts) return null;
  return country === "CA" ? cachedAccounts.ca : cachedAccounts.us;
}

/**
 * Resolve the platform account ID from a currency code.
 * CAD → CA, USD → US, everything else → null.
 */
export function getPlatformAccountIdFromCurrency(
  currency: string | null | undefined,
): string | null {
  if (!currency) return null;
  const upper = currency.toUpperCase();
  if (upper === "CAD") return getPlatformAccountId("CA");
  if (upper === "USD") return getPlatformAccountId("US");
  return null;
}
